import { motion } from "framer-motion";
import { UtensilsCrossed, Disc3, MoonStar } from "lucide-react";
import { useLang } from "@/App";
import { copy, CONTACT } from "@/i18n";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const PHOTOS = [
    "/media/deepsociety-1.webp",
    "/media/deepsociety-2.webp",
    "/media/deepsociety-3.webp",
    "/media/deepsociety-4.webp",
    "/media/deepsociety-5.webp",
    "/media/deepsociety-6.webp",
    "/media/deepsociety-7.webp",
    "/media/deepsociety-8.webp",
    "/media/deepsociety-9.webp",
    "/media/deepsociety-neon.webp",
    "/media/deepsociety-10.webp",
    "/media/deepsociety-11.webp",
    "/media/deepsociety-12.webp",
    "/media/deepsociety-13.webp",
    "/media/deepsociety-14.webp",
];

const CHIP_ICONS = [UtensilsCrossed, Disc3, MoonStar];

const DeepSociety = () => {
    const { lang } = useLang();
    const c = copy[lang].deepSociety;

    return (
        <section id="events" data-testid="deep-society-section" className="relative py-20 lg:py-32 overflow-hidden bg-[#0A0614]">
            <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-[#7C3AED]/20 blur-[140px] pointer-events-none" />
            <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#2563EB]/20 blur-[140px] pointer-events-none" />
            <div className="absolute top-1/3 right-1/4 w-[380px] h-[380px] rounded-full bg-crimson/10 blur-[120px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="text-center"
                >
                    <span className="inline-flex items-center gap-2.5 text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#A78BFA]">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A78BFA] opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#A78BFA]"></span>
                        </span>
                        {c.label}
                    </span>
                    <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-4xl sm:text-6xl lg:text-7xl bg-gradient-to-r from-[#A78BFA] via-white to-[#60A5FA] bg-clip-text text-transparent">
                        Deep Society
                    </h2>
                    <p className="mt-2 font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-brass">× The Market</p>
                    <p className="mt-6 text-neutral-300 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">{c.text}</p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        {c.chips.map((chip, i) => {
                            const Icon = CHIP_ICONS[i];
                            return (
                                <span
                                    key={i}
                                    data-testid={`deep-society-chip-${i}`}
                                    className="inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#C4B5FD] text-xs font-semibold uppercase tracking-widest px-4 py-2"
                                >
                                    <Icon size={14} />
                                    {chip}
                                </span>
                            );
                        })}
                    </div>
                </motion.div>

                <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                    {PHOTOS.map((src, i) => (
                        <motion.div
                            key={i}
                            data-testid={`deep-society-photo-${i}`}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className={`rounded-2xl overflow-hidden border border-white/10 group ${i % 2 === 1 ? "lg:mt-10" : ""}`}
                        >
                            <img
                                src={src}
                                alt={`Deep Society × The Market — ${c.label}`}
                                className="w-full h-full object-cover aspect-[3/4] transition-transform duration-500 group-hover:scale-105"
                                loading="lazy"
                            />
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="mt-12 text-center"
                >
                    <a
                        data-testid="deep-society-whatsapp-cta"
                        href={CONTACT.whatsappDs}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 rounded-full bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-xs sm:text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 transition-colors shadow-[0_0_40px_-8px_rgba(124,58,237,0.6)]"
                    >
                        <WhatsAppIcon size={16} />
                        {c.cta}
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default DeepSociety;
